// SEO 정본 도메인과 프록시 대상 호스트를 한 곳에서 관리한다.
// framer 프록시, sitemap/robots 라우트가 모두 이 값을 참조한다.

const stripTrailingSlash = (origin: string) => origin.replace(/\/+$/, '');

// 정본(canonical) 도메인. canonical·og:url 이 이미 이 값을 쓰고 있어
// 도메인 이관은 Vercel 의 NEXT_PUBLIC_PAGE_URL 교체만으로 끝나야 한다.
export const SITE_ORIGIN = stripTrailingSlash(
  process.env.NEXT_PUBLIC_PAGE_URL || 'https://hanghae.co',
);

export const SITE_HOST = SITE_ORIGIN.replace(/^https?:\/\//, '');

// 이관 전 도메인. vercel.json 이 301 로 넘기지만 Framer HTML·인블로그 sitemap 안에는
// 그대로 남아 있어 프록시 단계에서 정본 도메인으로 치환한다.
export const LEGACY_HOSTS = [
  'hanghae99.spartaclub.kr',
  'hanghae99.spartacodingclub.kr',
];

// Framer 퍼블리시 호스트. Framer 가 canonical 을 자기 도메인으로 내보내므로 함께 치환한다.
export const FRAMER_PUBLISH_HOST = 'hanghae99.framer.website';

// 인블로그 sitemap 원본. /blog 는 이 쪽으로 rewrite 된다.
export const INBLOG_SITEMAP_URL = 'https://inblog.ai/hanghae99/sitemap.xml';

// SSRF 방지: Framer 도메인만 프록시를 허용한다.
export const isAllowedFramerHost = (host: string) =>
  host.endsWith('.framer.app') || host.endsWith('.framer.website');

// 호스트명의 점을 문자 클래스로 바꿔 정규식 리터럴로 만든다.
// 호스트는 영숫자·점·하이픈뿐이라 점만 막으면 된다.
const toHostPattern = (host: string) => host.split('.').join('[.]');

// 호스트 경계. 앞에 영숫자·점·하이픈이 오면 다른 호스트이므로
// (testhanghae99.spartaclub.kr 등) 치환 대상에서 제외한다.
const HOST_BOUNDARY = '(?<![A-Za-z0-9_.-])';

// 치환 후보 호스트.
const PROXIED_HOSTS = [FRAMER_PUBLISH_HOST, ...LEGACY_HOSTS].map((host) => ({
  host,
  // 프로토콜까지 포함해 치환하면 http → https 정규화도 함께 처리된다.
  originPattern: new RegExp(
    `${HOST_BOUNDARY}https?://${toHostPattern(host)}`,
    'g',
  ),
  // 프로토콜 없이 호스트명만 쓰인 경우 (구조화 데이터의 alternateName 등)
  hostPattern: new RegExp(`${HOST_BOUNDARY}${toHostPattern(host)}`, 'g'),
}));

/**
 * 문서에 남아 있는 Framer 퍼블리시 도메인·구 도메인을 targetOrigin 으로 치환한다.
 *
 * canonical 태그를 self URL 로 덮어쓰지 않고 도메인만 바꾸는 이유:
 * /plus-lite/be/secret, /plus-lite/be/middle 은 Framer 에서 의도적으로
 * canonical 을 /plus-lite/be 로 모아둔 변형 페이지다. self canonical 을 주입하면
 * 이 통합 의도가 깨져 중복 색인이 생긴다. 경로는 원본을 존중하고 도메인만 교정한다.
 *
 * targetOrigin 은 이관 기간 중 구 도메인이 자기 URL 로 된 sitemap 을 내보내야 해서
 * 열어두었다. 기본값은 정본 도메인이다.
 */
export const replaceProxiedOrigins = (
  input: string,
  targetOrigin: string = SITE_ORIGIN,
) => {
  const targetHost = targetOrigin.replace(/^https?:\/\//, '');
  return PROXIED_HOSTS.filter(({ host }) => host !== targetHost).reduce(
    (acc, { originPattern, hostPattern }) =>
      acc.replace(originPattern, targetOrigin).replace(hostPattern, targetHost),
    input,
  );
};

/**
 * 정본 도메인 기준 canonical URL.
 * 원본에 canonical 이 아예 없을 때의 폴백으로만 쓴다.
 * path 는 요청에서 파생되므로 encodeURI 로 " < > 에 의한 HTML 속성 이탈을 막는다.
 */
export const buildCanonical = (pathname: string) => {
  const clean = `/${pathname}`.replace(/\/+$/, '');
  return `${SITE_ORIGIN}${clean === '' ? '/' : encodeURI(clean)}`;
};

// 이관 기간 한시적 처리.
// 구 도메인도 자기 URL 로 된 sitemap/robots 를 계속 내보내야 크롤러가 구 URL 을
// 재크롤하면서 301 을 발견하고 이전을 인식한다. 구 도메인 sitemap 까지 301 로 넘기면
// 재크롤할 URL 목록 자체가 사라진다.
// 구글이 이전을 인식한 뒤(GSC 주소변경 도구 병행 시 통상 수개월) 이 분기를 제거한다.
export const resolveRequestOrigin = (host?: string) => {
  const hostname = (host || '').split(':')[0].toLowerCase();
  return LEGACY_HOSTS.includes(hostname) ? `https://${hostname}` : SITE_ORIGIN;
};
